import { isMp4File } from "@/lib/utils";
import { Attachment } from "@/types/fb-type";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { VoiceMessagePlayer } from "./VoiceMessagePlayer";
import { Loader2, Image as ImageIcon } from "lucide-react";

export interface AttachmentInput {
    attachment: Attachment;
    userId?: string;
    isCustomer?: boolean;
}

export function AttachmentView({ att }: { att: AttachmentInput }) {
    const attachment = att.attachment;
    const rawAtt = attachment as any;
    const mimeType = (attachment.mimeType || rawAtt.mime_type || "").toLowerCase();
    const type = (rawAtt.type || "").toLowerCase();

    // Check all possible places where the media URL could reside
    const resolvedUrl =
        attachment.fileUrl ||
        rawAtt.file_url ||
        attachment.imageData?.url ||
        attachment.imageData?.previewUrl ||
        rawAtt.image_data?.url ||
        rawAtt.image_data?.preview_url ||
        rawAtt.audioData?.url ||
        rawAtt.audio_data?.url ||
        rawAtt.videoData?.url ||
        rawAtt.video_data?.url ||
        rawAtt.url ||
        rawAtt.payload?.url ||
        "";

    const isImage =
        attachment.image === true ||
        type === "image" ||
        type === "photo" ||
        !!attachment.imageData?.url ||
        !!rawAtt.image_data?.url ||
        mimeType.startsWith("image/") ||
        /\.(heic|heif|jpg|jpeg|png|webp|gif|svg)($|\?)/i.test(resolvedUrl);

    // Facebook returns voice clips as .mp4 files (often tagged video/mp4) named "audioclip-*".
    const isVoiceClip = /audioclip|voice[-_]/i.test(`${rawAtt.name || ""} ${resolvedUrl}`);

    const isAudio =
        !isImage &&
        (isVoiceClip ||
            attachment.audio === true ||
            type === "audio" ||
            type === "voice" ||
            !!rawAtt.audioData ||
            !!rawAtt.audio_data ||
            mimeType.startsWith("audio/") ||
            (isMp4File(resolvedUrl) && !attachment.video && type !== "video") ||
            /\.(mp3|wav|webm|ogg|m4a|aac|opus)($|\?)/i.test(resolvedUrl));

    const isVideo =
        !isImage &&
        !isAudio &&
        (attachment.video === true ||
            type === "video" ||
            !!rawAtt.videoData ||
            !!rawAtt.video_data ||
            mimeType.startsWith("video/") ||
            /\.(mp4|mov|avi|wmv|flv|mkv|webm)($|\?)/i.test(resolvedUrl));

    // -------- Image with Dialog (Lightbox) --------
    if (isImage) {
        if (resolvedUrl) {
            const fullUrl =
                attachment.imageData?.url ||
                rawAtt.image_data?.url ||
                resolvedUrl;
            const thumbUrl =
                attachment.imageData?.previewUrl ||
                rawAtt.image_data?.preview_url ||
                fullUrl;
            const alt = attachment.imageData?.renderAsSticker ? "Sticker" : "Photo";

            return (
                <ImageLightbox src={fullUrl} alt={alt}>
                    {({ onClick }) => (
                        <button
                            type="button"
                            onClick={onClick}
                            className="group relative block max-w-full text-left focus:outline-none"
                            aria-label="View photo"
                        >
                            <img
                                src={thumbUrl}
                                loading="lazy"
                                alt={alt}
                                className="max-h-56 sm:max-h-72 w-auto max-w-full rounded-xl object-cover cursor-pointer hover:brightness-95 transition shadow-sm"
                            />
                            {/* subtle zoom affordance */}
                            <span className="absolute inset-0 rounded-xl ring-0 ring-blue-300/0 group-hover:ring-2 group-hover:ring-blue-300/40 transition pointer-events-none" />
                        </button>
                    )}
                </ImageLightbox>
            );
        }

        // Image attachment exists but URL is still processing/indexing on Facebook CDN
        return (
            <div className="flex items-center gap-2 py-3 px-4 rounded-xl bg-black/5 dark:bg-white/5 border border-dashed border-gray-300 text-xs text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin text-blue-500 shrink-0" />
                <span>Processing photo...</span>
            </div>
        );
    }

    // -------- Voice / Audio --------
    if (isAudio && resolvedUrl) {
        return (
            <VoiceMessagePlayer
                src={resolvedUrl}
                isCustomer={att.isCustomer}
                duration={rawAtt.audioData?.duration || rawAtt.audio_data?.duration}
            />
        );
    }

    // -------- Video --------
    if (isVideo && resolvedUrl) {
        return (
            <video
                controls
                preload="metadata"
                src={resolvedUrl}
                poster={attachment.imageData?.previewUrl || rawAtt.image_data?.preview_url}
                className="rounded-lg my-2 max-w-full"
            />
        );
    }

    // -------- Fallback: link --------
    if (resolvedUrl) {
        return (
            <a
                href={resolvedUrl}
                target="_blank"
                rel="noreferrer"
                className={`underline break-all text-xs font-medium inline-flex items-center gap-1 ${
                    att.isCustomer ? "text-blue-600 hover:text-blue-800" : "text-white/90 hover:text-white"
                }`}
            >
                📎 {rawAtt.name || attachment.mimeType || "Download attachment"}
            </a>
        );
    }

    return null;
}